import { useLayoutEffect, useState } from 'react'

import { useTheme } from '@mui/material/styles'
import { observer } from 'mobx-react-lite'

import type { Beat, DataSeries, Transaction } from '../../../core'
import { formatCurrency } from '../../../core/utils/NumberFormatting'
import { BarChartV2 } from '../../components/chartblocks/BarchartV2'
import { EmptyBarChart } from '../../components/chartblocks/EmptyBarChart'

import { TransactionDrillDown } from './TransactionDrillDown'
import { useTransactionHistory } from './useTransactionHistory'
import {
  getDefaultSelection,
  getDrillDownConfig,
  getFlattenedDataSeries,
  toStoreFilter,
} from './utils'
import {
  Drawer,
  useInsightsV2Store,
  useGlobalCopyStore,
  useGlobalUiStore,
  Loader,
} from '../../../common'
import { TransactionDetailsBlock } from '../TransactionDetailsBlock'

interface TransactionHistoryProp {
  beat: Beat
}

export const TransactionHistoryBlock = observer(({ beat }: TransactionHistoryProp) => {
  const theme = useTheme()
  const {
    instanceSlotData,
    beatTransactions,
    spendCategoryGuids,
    allAccountGuids,
    categoryName,
    displayedDateRange,
    transactionStore,
  } = useTransactionHistory(beat)

  const { actionDrawer, setActionContext } = useInsightsV2Store()

  const { common: commonCopy, insights_feed: insightsCopy } = useGlobalCopyStore()

  const { isCopyLoaded } = useGlobalUiStore()

  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(false)

  const [closeTipSection, setCloseTipSection] = useState(false)

  const { insights_payload } = beat

  const dynamicCtaVariant =
    actionDrawer.trigger === 'secondaryCta'
      ? insights_payload?.secondary_cta?.variant
      : insights_payload?.primary_cta?.variant

  const { top, filterMode, header, colors, filterStrategy, tipSection } = getDrillDownConfig(
    dynamicCtaVariant ?? '',
  )

  const { dataSeries, chartData, comparisonBars } = getFlattenedDataSeries({
    data: instanceSlotData?.data_series as DataSeries,
    ariaLabel: instanceSlotData?.ariaLabel ?? '',
    top,
    beatTransactions,
    payload: instanceSlotData,
  })

  const { chartBarIndex, subTabIndex } = getDefaultSelection({
    chartTabCount: chartData.length,
    comparisonTabCount: comparisonBars.length,
    actionDrawer,
    beatGuid: beat.guid,
  })

  const [selectedIndex, setSelectedIndex] = useState(chartBarIndex)

  const resolveFilter = (index: number) =>
    filterStrategy({
      beat,
      beatTransactions,
      dataSeries,
      selectedIndex: index,
      subIndex: subTabIndex,
      isLastTab: index === dataSeries.length - 1,
      instanceSlotData,
      spendCategoryGuids,
      displayedDateRange,
    })

  // Apply the current selection's filter. Store variants route through the store filter; local
  // (upcomingBill) variants filter beatTransactions directly and only backfill the cache when the
  // Stable primitive keys for the collections below: beatTransactions/spendCategoryGuids/
  // allAccountGuids are recomputed (new array identity) every render, so depending on them directly
  // re-fires the effect each render (REVIEW.md MEW-3732). Key on content instead.
  const beatTransactionGuidsKey = beatTransactions.map((transaction) => transaction.guid).join(',')
  const spendCategoryGuidsKey = spendCategoryGuids.join(',')
  const allAccountGuidsKey = allAccountGuids.join(',')

  // Re-applies the current selection's filter to the shared transactionStore. Called both by the
  // effect below (on selection change) and when the nested TransactionDetailsBlock drawer closes:
  // AddMerchantBudgetAction (opened from within that drawer) overwrites transactionStore.filter
  // with a merchant_guid-only filter while its own drawer is open, and never restores it — leaving
  // the row list stuck showing only that one merchant's transactions after backing all the way out.
  // Re-running this on close re-derives the filter fresh instead of relying on whatever the nested
  // drawers left behind.
  const applyCurrentSelectionFilter = () => {
    if (filterMode !== 'store' || !dataSeries.length) return
    transactionStore.setFilter(
      toStoreFilter(resolveFilter(selectedIndex), { allAccountGuids, displayedDateRange }),
    )
  }

  // store variants route the selection through the shared store filter; local (upcomingBill) variants
  // filter beatTransactions directly, so there's nothing to push to the store for them.
  useLayoutEffect(() => {
    applyCurrentSelectionFilter()
  }, [
    beat.guid,
    selectedIndex,
    filterMode,
    dataSeries.length,
    instanceSlotData,
    displayedDateRange,
    spendCategoryGuidsKey,
    allAccountGuidsKey,
    beatTransactionGuidsKey,
  ])

  const headerContent = header?.({
    title: insights_payload?.title,
    subHeader: insights_payload?.sub_title,
    categoryName,
    selectedDate: chartData[selectedIndex]?.date ?? undefined,
  })

  const barColors = colors(theme, instanceSlotData?.category_guid)

  if (!isCopyLoaded) return <Loader />

  // TODO: Temporary fallback while the backend doesn't always send `legends` on every beat/variant.
  // Remove this once the backend guarantees `legends` on the payload.
  const legendFallbackLabels = [insightsCopy.general.monthly_amount, insightsCopy.general.trend]

  const [monthlyAmountLabel, trendAmountLabel] = instanceSlotData?.legends?.length
    ? instanceSlotData.legends.map((legend) => legend.label)
    : legendFallbackLabels

  const transactionFilter =
    filterMode === 'local'
      ? (index: number) => resolveFilter(index).custom ?? (() => true)
      : undefined

  const baseTransactions: Transaction[] | undefined =
    filterMode === 'local' ? beatTransactions : undefined

  const isBarTop = top === 'singleBar' || top === 'upcomingBill'
  const chartTop = !isBarTop ? null : chartData.length ? (
    <BarChartV2
      amounts={{
        average: instanceSlotData?.average_amount,
        formattedAverage: formatCurrency(instanceSlotData?.average_amount ?? 0, '0,0.00'),
      }}
      color={barColors[0] ?? theme.palette.primary.main}
      data={chartData}
      legendPosition="top"
      margin={{
        bottom: 30,
        top: 20,
        left: 24,
        right: 24,
      }}
      monthlyAmountLabel={monthlyAmountLabel}
      onBarClick={setSelectedIndex}
      selectedTabIndex={selectedIndex}
      trendAmountLabel={trendAmountLabel}
    />
  ) : (
    <EmptyBarChart />
  )

  return (
    <>
      <TransactionDrillDown
        ariaLabel={instanceSlotData?.ariaLabel}
        baseTransactions={baseTransactions}
        heading={headerContent?.heading}
        icon={headerContent?.icon}
        onSelectionChange={setSelectedIndex}
        selectedIndex={selectedIndex}
        subHeadingText={headerContent?.subHeadingText}
        tabs={chartData.map((bar) => ({
          ariaLabel: bar.ariaLabel,
          label: bar.label,
        }))}
        top={chartTop}
        {...(tipSection && {
          tipSection:
            !closeTipSection &&
            tipSection(setCloseTipSection, insightsCopy, insights_payload?.sub_title?.text),
        })}
        transactionFilter={transactionFilter}
        onEachRowClick={(guid) => {
          setActionContext({ transaction_guid: guid })
          setIsTransactionDetailsOpen(true)
        }}
      />
      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={isTransactionDetailsOpen}
        onClose={() => {
          setIsTransactionDetailsOpen(false)
          // Clear only the key this nested drawer owns — setActionContext merges, so an
          // explicit `{}` would be a no-op and leave transaction_guid stuck in the shared
          // context, and the outer drawer's own context (e.g. chartBarIndex) must survive.
          setActionContext({ transaction_guid: undefined })
          // Re-apply this block's own filter: a further-nested AddMerchantBudgetAction drawer may
          // have overwritten transactionStore.filter (merchant_guid-only) without restoring it.
          applyCurrentSelectionFilter()
        }}
        title={insightsCopy.general.transaction_details_title}
      >
        <TransactionDetailsBlock beat={beat} />
      </Drawer>
    </>
  )
})
